/**
 * Making, renaming, copying and throwing away a view.
 *
 * A view is the only way to look at a table -- the rows endpoint is asked for
 * one -- so a table with no views is a table nobody can read. Until now there
 * was no way to make one, which made "this table has no views" a message with
 * no way out of it.
 *
 * **The cached list is patched rather than invalidated**, for the reason the
 * sorts and filters are: the answer says exactly what changed, and re-reading
 * every view of the table to learn it would be a round trip that tells us
 * nothing new. The acting tab is excluded from its own `view_created`,
 * `view_updated` and `view_deleted` broadcasts, so nothing is coming over the
 * socket to do it instead.
 *
 * **Details are patched field by field, never by replacing the view.** The
 * endpoint answers with a view serialised *without* its filters and sortings --
 * which is also what its broadcast carries, and for the same stated reason: the
 * filters have their own endpoints and their own events. Writing the answer
 * back would rename the view and quietly unfilter it.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { View, ViewTypeName } from '@/api/types'
import { patchView, withViewOrder } from './view-cache'

export interface ViewMutations {
  create: (name: string, type: ViewTypeName, onCreated: (view: View) => void) => void
  updateDetails: (viewId: number, details: { name: string; description: string }) => void
  duplicate: (viewId: number, onCreated: (view: View) => void) => void
  remove: (viewId: number, onRemoved: () => void) => void
  reorder: (viewIds: number[]) => void
  isPending: boolean
}

export function useViewMutations(tableId: number): ViewMutations {
  const queryClient = useQueryClient()
  const key = qk.views(tableId)

  const append = (view: View): void => {
    queryClient.setQueryData<View[]>(key, (views) =>
      views === undefined ? views : [...views, view],
    )
  }

  const create = useMutation({
    mutationFn: (body: { name: string; type: ViewTypeName }): Promise<View> =>
      unwrap(
        getApi().POST('/api/database/views/table/{table_id}/', {
          params: { path: { table_id: tableId } },
          // Cast at the boundary, like every view payload: the body is a union
          // over the seven view types. `ownership_type` is left out because
          // this fork registers only one -- the personal-view manager went with
          // the enterprise tree -- so the backend's default is the only answer.
          body: body as never,
        }),
      ),
    onSuccess: append,
  })

  const duplicate = useMutation({
    mutationFn: (viewId: number): Promise<View> =>
      unwrap(
        getApi().POST('/api/database/views/{view_id}/duplicate/', {
          params: { path: { view_id: viewId } },
        }),
      ),
    // The copy comes back whole -- filters, sortings and all -- because it is a
    // view being created rather than one being changed.
    onSuccess: append,
  })

  const updateDetails = useMutation({
    mutationFn: ({
      viewId,
      details,
    }: {
      viewId: number
      details: { name: string; description: string }
    }): Promise<unknown> =>
      unwrap(
        getApi().PATCH('/api/database/views/{view_id}/', {
          params: { path: { view_id: viewId } },
          body: details as never,
        }),
      ),
    onMutate: ({ viewId, details }) => {
      // Applied at once: neither is a question about the rows, so there is
      // nothing to refetch and nothing to be wrong about while waiting.
      const previous = queryClient.getQueryData<View[]>(key)
      queryClient.setQueryData<View[]>(key, (views) =>
        views === undefined
          ? views
          : patchView(views, viewId, (view) => ({ ...view, ...details })),
      )
      return { previous }
    },
    onError: (_error, _variables, context) => {
      queryClient.setQueryData<View[] | undefined>(key, context?.previous)
    },
  })

  const remove = useMutation({
    mutationFn: (viewId: number): Promise<unknown> =>
      unwrap(
        getApi().DELETE('/api/database/views/{view_id}/', {
          params: { path: { view_id: viewId } },
        }),
      ),
    // Not optimistic, and not because a view is precious: whoever deleted it is
    // looking at it, so the page has to move somewhere else first. Doing that
    // before the server has agreed would leave them on another view with the
    // deleted one still in the list when the request was refused.
    onSuccess: (_answer, viewId) => {
      queryClient.setQueryData<View[]>(key, (views) =>
        views === undefined ? views : views.filter((view) => view.id !== viewId),
      )
    },
  })

  const reorder = useMutation({
    mutationFn: (viewIds: number[]): Promise<unknown> =>
      unwrap(
        getApi().POST('/api/database/views/table/{table_id}/order/', {
          params: { path: { table_id: tableId } },
          body: { view_ids: viewIds },
        }),
      ),
    onMutate: (viewIds) => {
      // Applied at once, like a rename and unlike a delete: a new order is not
      // a question about the rows, and a list that snapped back to where it was
      // for a round trip after a drag would read as a drag that failed.
      const previous = queryClient.getQueryData<View[]>(key)
      queryClient.setQueryData<View[]>(key, (views) =>
        views === undefined ? views : withViewOrder(views, viewIds),
      )
      return { previous }
    },
    onError: (_error, _viewIds, context) => {
      queryClient.setQueryData<View[] | undefined>(key, context?.previous)
    },
  })

  return {
    create: (name, type, onCreated) => {
      create.mutate({ name, type }, { onSuccess: onCreated })
    },
    updateDetails: (viewId, details) => {
      updateDetails.mutate({ viewId, details })
    },
    duplicate: (viewId, onCreated) => {
      duplicate.mutate(viewId, { onSuccess: onCreated })
    },
    remove: (viewId, onRemoved) => {
      remove.mutate(viewId, { onSuccess: onRemoved })
    },
    reorder: (viewIds) => {
      reorder.mutate(viewIds)
    },
    isPending: create.isPending || duplicate.isPending || remove.isPending,
  }
}
